/**
 * Pure pack pricing and vial eligibility utilities for TEK TIDES LABS.
 *
 * NOTE ON TIER PRICING / DISCOUNTS:
 * WooCommerce server-side quantity discount rules (3-4 vials 10%, 5-9 15%, 10+ 20%;
 * WELCOME25 coupon replaces quantity savings) are active and verified.
 * Products are added to cart at standard catalog prices, and the server snippet
 * applies the discount to the cart. calculatePack provides pre-cart estimates only.
 */

import type { EcommerceProduct } from "@/integrations/wordpress/types";

/**
 * Flag indicating whether WooCommerce server-side discounts are live and verified.
 */
export const PACK_DISCOUNTS_ENABLED = true;

/**
 * Flag controlling whether planned discount tier percentages and estimated savings
 * are displayed as a preview to users.
 */
export const PACK_DISCOUNT_PREVIEW_ENABLED = true;

/**
 * Standard label displayed next to discount estimates in the pack builder.
 */
export const ESTIMATE_LABEL = "Estimated savings";

/**
 * Note explaining that client-side savings are estimates only and confirmed in the cart.
 */
export const ESTIMATE_NOTE =
  "Estimate only. Your existing cart or a WELCOME25 code can change the final discount, which is confirmed in your cart.";

export interface PackTier {
  minVials: number;
  maxVials: number | null;
  discountPercent: number;
}

/**
 * Tier table:
 * 3–4 vials: 10%
 * 5–9 vials: 15%
 * 10+ vials: 20%
 * Highest qualifying tier only.
 */
export const PACK_TIERS: PackTier[] = [
  { minVials: 3, maxVials: 4, discountPercent: 10 },
  { minVials: 5, maxVials: 9, discountPercent: 15 },
  { minVials: 10, maxVials: null, discountPercent: 20 },
];

export interface VialEligibility {
  eligible: boolean;
  reason?: string;
}

/**
 * Determines whether a product is an eligible single-vial research compound.
 *
 * Excluded:
 * 1. Category name or slug matches /accessor/i or /internal/i
 * 2. Multi-vial combos (title or sku with more than one mg amount joined by + or / or & e.g. "10 mg + 10 mg")
 * 3. Product type is "grouped" or "bundle"
 */
export function isEligibleVial(product: any): VialEligibility {
  if (!product || typeof product !== "object") {
    return { eligible: false, reason: "Invalid product record" };
  }

  // Check product type
  const productType = String(product?.type || "").toLowerCase();
  if (productType === "grouped" || productType === "bundle") {
    return { eligible: false, reason: "Grouped or bundled product type" };
  }

  // Check categories for /accessor/i or /internal/i
  const categories = Array.isArray(product?.categories) ? product.categories : [];
  for (const cat of categories) {
    const name = String(cat?.name || "");
    const slug = String(cat?.slug || "");
    if (/accessor/i.test(name) || /accessor/i.test(slug)) {
      return { eligible: false, reason: "Accessories category" };
    }
    if (/internal/i.test(name) || /internal/i.test(slug)) {
      return { eligible: false, reason: "Internal category" };
    }
  }

  // Check title & sku for multi-vial combo markers (e.g. "10 mg + 10 mg" or "BPC-157 + TB-500")
  const title = String(product?.title || product?.name || "");
  const sku = String(product?.sku || "");
  const textToCheck = `${title} ${sku}`;

  // Multi-mg combo detection: e.g. "10 mg + 10 mg", "10mg/10mg", "10mg & 10mg"
  const multiMgRegex = /\d+(?:\.\d+)?\s*mg\s*[+\/&]\s*\d+(?:\.\d+)?\s*mg/i;
  if (multiMgRegex.test(textToCheck)) {
    return { eligible: false, reason: "Multi-vial combination compound" };
  }

  // Also check if title contains " + " with two compounds (e.g. "BPC-157 + TB-500")
  if (/\b\w+[\w-]*\s*\+\s*\w+/i.test(title) && /\bmg\b/i.test(title)) {
    // If it has multiple mg occurrences or explicit plus
    const mgMatches = Array.from(title.matchAll(/\d+(?:\.\d+)?\s*mg\b/gi));
    if (mgMatches.length > 1) {
      return { eligible: false, reason: "Multi-vial combination compound" };
    }
  }

  return { eligible: true };
}

export interface ExcludedReviewItem {
  id: string | number;
  title: string;
  reason: string;
}

/**
 * Returns a list of excluded products with their reasons so owner can review.
 */
export function getExcludedForReview(products: any[]): ExcludedReviewItem[] {
  if (!Array.isArray(products)) return [];
  const list: ExcludedReviewItem[] = [];

  for (const p of products) {
    const eligibility = isEligibleVial(p);
    if (!eligibility.eligible) {
      list.push({
        id: p?.id ?? p?.ID ?? "unknown",
        title: String(p?.title || p?.name || "Untitled"),
        reason: eligibility.reason || "Not eligible",
      });
    }
  }

  return list;
}

export interface PackLineItem {
  key: string;
  productId: number;
  variationId?: number;
  title: string;
  displayTitle: string;
  strength: string;
  unitPrice: number; // in dollars / major units
  quantity: number;
  imageUrl?: string;
  imageAlt: string;
  eligible: boolean;
  maxQuantity?: number;
  currencySymbol?: string;
}

export interface PackCalculation {
  eligibleVials: number;
  subtotal: number; // in dollars
  subtotalCents: number;
  tier: PackTier | null;
  discountPercent: number;
  savings: number; // in dollars
  savingsCents: number;
  discountedTotal: number; // in dollars
  discountedTotalCents: number;
  vialsToNextTier: number;
  nextTierPercent: number | null;
}

/**
 * Pure calculation function for pack totals using integer cents.
 */
export function calculatePack(lines: PackLineItem[]): PackCalculation {
  let eligibleVials = 0;
  let subtotalCents = 0;

  for (const line of lines) {
    const qty = Math.max(0, Math.floor(line.quantity || 0));
    if (line.eligible) {
      eligibleVials += qty;
      const unitCents = Math.round((line.unitPrice || 0) * 100);
      subtotalCents += unitCents * qty;
    }
  }

  // Determine matching tier (highest qualifying)
  let qualifyingTier: PackTier | null = null;
  for (const tier of PACK_TIERS) {
    if (eligibleVials >= tier.minVials) {
      if (tier.maxVials === null || eligibleVials <= tier.maxVials) {
        qualifyingTier = tier;
      } else if (eligibleVials > tier.maxVials) {
        qualifyingTier = tier; // higher tiers come later in PACK_TIERS
      }
    }
  }

  const discountPercent = qualifyingTier?.discountPercent ?? 0;
  const savingsCents = Math.round((subtotalCents * discountPercent) / 100);
  const discountedTotalCents = subtotalCents - savingsCents;

  // Determine next tier if any
  let vialsToNextTier = 0;
  let nextTierPercent: number | null = null;

  for (const tier of PACK_TIERS) {
    if (eligibleVials < tier.minVials) {
      vialsToNextTier = tier.minVials - eligibleVials;
      nextTierPercent = tier.discountPercent;
      break;
    }
  }

  return {
    eligibleVials,
    subtotal: subtotalCents / 100,
    subtotalCents,
    tier: qualifyingTier,
    discountPercent,
    savings: savingsCents / 100,
    savingsCents,
    discountedTotal: discountedTotalCents / 100,
    discountedTotalCents,
    vialsToNextTier,
    nextTierPercent,
  };
}
